import {
  createTheme,
  defaultVariantColorsResolver,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from "@mantine/core";
import { AreaChart, BarChart, CompositeChart, DonutChart, LineChart } from "@mantine/charts";
import { RAMPS, TOKENS } from "./theme/tokens";

// Smart Safiri colour system — see src/theme/tokens.ts and
// smart-safiri-dashboard-colour.md. Mantine's named colours are remapped onto
// the spec's ramps so every existing `color="red"`, `c="dimmed"`, badge and
// button lands on a measured token without touching each screen.

const O = RAMPS.oxblood;
const oxblood: MantineColorsTuple = [O[50], O[100], O[200], O[300], O[400], O[500], O[600], O[700], O[800], O[900]];

// Light neutrals (gunmetal-tinted, never pure grey).
const gray: MantineColorsTuple = ["#FAFBFB", "#F4F6F6", "#EAEAEA", "#DDE0E0", "#C3C8C9", "#9AA1A2", "#767E7F", "#626A6B", "#545B5C", "#1E2223"];
// Mantine's dark scale: [0] text … [7] card … [8] canvas … [9] sunken.
const dark: MantineColorsTuple = ["#ECEEEF", "#C4CACB", "#A4ACAD", "#848C8E", "#5E6668", "#343A3C", "#1A1E1F", "#131617", "#0C0E0F", "#0A0B0C"];

// Status ramps (index 6 = light solid, 7 = light fg, 4/5 = dark fg/solid).
const error: MantineColorsTuple = ["#FEF3F2", "#FEE4E2", "#FECDCA", "#FDA29B", "#F97066", "#F04438", "#D92D20", "#B42318", "#912018", "#7A271A"];
const success: MantineColorsTuple = ["#ECFDF3", "#DCFAE6", "#ABEFC6", "#75E0A7", "#47CD89", "#17B26A", "#079455", "#067647", "#085D3A", "#074D31"];
const warning: MantineColorsTuple = ["#FFFAEB", "#FEF0C7", "#FEDF89", "#FEC84B", "#FDB022", "#F79009", "#DC6803", "#8A5300", "#93370D", "#7A2E0E"];
const info: MantineColorsTuple = ["#EFF8FF", "#D1E9FF", "#B2DDFF", "#84CAFF", "#84ADFF", "#2E90FA", "#1570EF", "#175CD3", "#1849A9", "#194185"];
const steel: MantineColorsTuple = ["#F0F7F8", "#E3F0F2", "#C2DFE3", "#9BCBD1", "#6FB3BA", "#4A9199", "#2F6F76", "#245C62", "#1B474C", "#133336"];

/** Mantine colour name → spec token family for light/subtle badges. */
const FAMILY: Record<string, string> = {
  red: "error",
  green: "success",
  teal: "success",
  yellow: "warning",
  orange: "warning",
  blue: "info",
  gray: "state-offline",
  steel: "accent-driver",
  oxblood: "brand-subtle",
  safiri: "brand-subtle",
};

const variantColorResolver: VariantColorsResolver = (input) => {
  const base = defaultVariantColorsResolver(input);
  const color = input.color ?? input.theme.primaryColor;
  const family = FAMILY[color];

  if (input.variant === "light" && family) {
    const fg = family === "accent-driver" ? "var(--accent-driver-fg)" : `var(--${family}-fg)`;
    const bg = `var(--${family}-bg)`;
    return { ...base, background: bg, hover: bg, color: fg, hoverColor: fg, border: "1px solid transparent" };
  }

  if (input.variant === "filled") {
    switch (color) {
      case "oxblood":
      case "safiri":
        return { ...base, background: "var(--action-primary-bg)", hover: "var(--action-primary-bg-hover)", color: "var(--text-on-brand)", hoverColor: "var(--text-on-brand)" };
      case "red":
        // Destructive: deliberately brighter than the brand oxblood.
        return { ...base, background: "var(--action-destructive-bg)", hover: "var(--action-destructive-bg-hover)", color: "#FFFFFF", hoverColor: "#FFFFFF" };
      case "yellow":
      case "orange":
        // Warning fills take dark ink — white on #F79009 fails (2.1:1).
        return { ...base, background: "var(--warning-solid)", hover: "var(--warning-solid)", color: "#1E2223", hoverColor: "#1E2223" };
      case "green":
      case "teal":
        return { ...base, background: "var(--success-solid)", hover: "var(--success-solid)", color: "#FFFFFF", hoverColor: "#FFFFFF" };
      case "blue":
        return { ...base, background: "var(--info-solid)", hover: "var(--info-solid)", color: "#FFFFFF", hoverColor: "#FFFFFF" };
      case "steel":
        return { ...base, background: "var(--accent-driver-solid)", hover: "var(--accent-driver-solid)", color: "#FFFFFF", hoverColor: "#FFFFFF" };
    }
  }

  if (input.variant === "default") {
    // Secondary button: needs a findable edge (border-interactive, ≥3:1).
    return {
      ...base,
      background: "var(--action-secondary-bg)",
      hover: "var(--action-secondary-bg-hover)",
      color: "var(--text-primary)",
      hoverColor: "var(--text-primary)",
      border: "1px solid var(--border-interactive)",
    };
  }

  if (input.variant === "subtle" && (color === "gray" || color === "dark")) {
    return { ...base, hover: "var(--action-ghost-bg-hover)" };
  }

  return base;
};

function schemeVars(scheme: "light" | "dark"): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, val] of Object.entries(TOKENS)) {
    out[`--${name}`] = typeof val === "string" ? val : val[scheme];
  }
  const t = (n: keyof typeof TOKENS) => out[`--${n}`];
  // Mantine's own variables, pointed at the tokens.
  Object.assign(out, {
    "--mantine-color-body": t("surface-default"),
    "--mantine-color-text": t("text-primary"),
    "--mantine-color-bright": t("text-primary"),
    "--mantine-color-dimmed": t("text-tertiary"),
    "--mantine-color-placeholder": t("text-disabled"),
    "--mantine-color-anchor": t("text-brand"),
    "--mantine-color-error": t("error-fg"),
    "--mantine-color-default": t("action-secondary-bg"),
    "--mantine-color-default-hover": t("action-secondary-bg-hover"),
    "--mantine-color-default-color": t("text-primary"),
    "--mantine-color-default-border": t("border-default"),
  });
  return out;
}

const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: schemeVars("light"),
  dark: schemeVars("dark"),
});

const chartDefaults = {
  defaultProps: { gridColor: "var(--viz-grid)", textColor: "var(--text-tertiary)" },
};

export const theme = createTheme({
  primaryColor: "oxblood",
  // oxblood-600 is the brand fill in light; dark steps to oxblood-500.
  primaryShade: { light: 6, dark: 5 },
  colors: {
    oxblood,
    safiri: oxblood,
    gray,
    dark,
    red: error,
    green: success,
    teal: success,
    yellow: warning,
    orange: warning,
    blue: info,
    steel,
  },
  variantColorResolver,
  // Type tokens aren't in the colour release yet (brand face is Lemon Milk);
  // Fira Sans with tabular figures until then.
  fontFamily: '"Fira Sans", Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: '"Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace',
  headings: { fontFamily: '"Fira Sans", Inter, system-ui, sans-serif', fontWeight: "650" },
  // Spec radius steps: sm 4 (badges), md 8 (buttons/inputs), lg 12 (cards).
  radius: { xs: "2px", sm: "4px", md: "8px", lg: "12px", xl: "16px" },
  defaultRadius: "md",
  spacing: { xs: "8px", sm: "12px", md: "16px", lg: "24px", xl: "32px" },
  shadows: {
    xs: "0 1px 2px rgba(30,34,35,0.06)",
    sm: "var(--shadow-raised)",
    md: "var(--shadow-raised)",
    lg: "var(--shadow-raised)",
    xl: "var(--shadow-raised)",
  },
  cursorType: "pointer",
  focusRing: "auto",
  components: {
    Card: { defaultProps: { withBorder: true, radius: "lg" } },
    Paper: { defaultProps: { radius: "lg" } },
    Badge: { defaultProps: { radius: "sm" } },
    Tooltip: { defaultProps: { withArrow: true, openDelay: 250 } },
    BarChart: BarChart.extend(chartDefaults),
    LineChart: LineChart.extend(chartDefaults),
    AreaChart: AreaChart.extend(chartDefaults),
    CompositeChart: CompositeChart.extend(chartDefaults),
    DonutChart: DonutChart.extend({ defaultProps: { strokeColor: "var(--surface-default)" } }),
  },
});

export { cssVariablesResolver };
